import { z } from "zod";
import type {
  LegendDocument,
  LegendPresetKey,
  LegendRow,
  LegendTile,
} from "../legend/document.js";
import type { NumberingScheme } from "../panel/numbering.js";
import type { TandemIdScheme } from "../panel/tandem.js";
import { CodecError, instantSchema } from "./envelope.js";

/**
 * Phase 7a codec for `LegendDocument` and its sub-types. Determinism
 * matches the rest of the codec: declared key order on every value,
 * arrays preserve their domain order, `undefined` optionals are
 * omitted. The discriminant `kind` is the first key on every tagged-
 * union value so the tagged-union encoding stays consistent with
 * panel / circuit / warning encoders.
 *
 * The codec validates rather than re-projects: a `LegendDocument` is
 * a derived artifact (built by `buildLegendDocument` from a `Panel`
 * + preset + warnings), so decoding does not need to re-run the
 * builder. Phase 7c's stale-legend hash treats the document as an
 * input alongside the live panel state; the codec's job here is to
 * preserve exact structure across persistence.
 */

const positionJsonSchema = z.object({
  row: z.number().int().nonnegative(),
  column: z.number().int().nonnegative(),
});

const numberingSchemeSchema: z.ZodType<NumberingScheme> = z.discriminatedUnion(
  "kind",
  [
    z.object({ kind: z.literal("sequential-down-then-over") }),
    z.object({ kind: z.literal("odd-left-even-right") }),
    z.object({ kind: z.literal("sequential-left-then-down") }),
    z.object({
      kind: z.literal("custom"),
      mapping: z.array(
        z.object({
          position: positionJsonSchema,
          number: z.number().int().positive(),
        }),
      ),
    }),
  ],
);

const tandemIdSchemeSchema: z.ZodType<TandemIdScheme> = z.enum([
  "ab-suffix",
  "dot-suffix",
  "tb-suffix",
]);

const legendPresetKeySchema: z.ZodType<LegendPresetKey> = z.enum([
  "homeline",
  "siemens",
  "generic-letter",
  "generic-a4",
]);

const circuitStatusSchema = z.enum(["in-service", "spare", "disconnected"]);

const legendTileJsonSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("circuit"),
    identifier: z.string().min(1),
    effectiveText: z.string(),
    status: circuitStatusSchema,
    hasOpenWarning: z.boolean(),
    installationGroupId: z.string().min(1),
    positionNumber: z.number().int(),
    pairedWith: z.string().min(1).optional(),
  }),
  z.object({
    kind: z.literal("continuation"),
    headIdentifier: z.string().min(1),
    installationGroupId: z.string().min(1),
    positionNumber: z.number().int(),
  }),
  z.object({
    kind: z.literal("empty"),
    positionNumber: z.number().int(),
  }),
]);

const legendRowJsonSchema = z.object({
  left: z.array(legendTileJsonSchema),
  right: z.array(legendTileJsonSchema).nullable(),
});

const legendLayoutSnapshotJsonSchema = z.object({
  columns: z.number().int().positive(),
  rows: z.number().int().positive(),
  numberingScheme: numberingSchemeSchema,
  tandemIdScheme: tandemIdSchemeSchema,
});

export const legendDocumentJsonSchema = z.object({
  panelId: z.string().min(1),
  layoutSnapshot: legendLayoutSnapshotJsonSchema,
  preset: legendPresetKeySchema,
  rows: z.array(legendRowJsonSchema),
  generatedAt: instantSchema,
});

export type LegendDocumentJson = z.infer<typeof legendDocumentJsonSchema>;

type LegendTileJson = z.infer<typeof legendTileJsonSchema>;
type LegendRowJson = z.infer<typeof legendRowJsonSchema>;

function encodeLegendTile(t: LegendTile): Record<string, unknown> {
  switch (t.kind) {
    case "circuit": {
      const out: Record<string, unknown> = {
        kind: "circuit",
        identifier: t.identifier,
        effectiveText: t.effectiveText,
        status: t.status,
        hasOpenWarning: t.hasOpenWarning,
        installationGroupId: t.installationGroupId,
        positionNumber: t.positionNumber,
      };
      if (t.pairedWith !== undefined) out["pairedWith"] = t.pairedWith;
      return out;
    }
    case "continuation":
      return {
        kind: "continuation",
        headIdentifier: t.headIdentifier,
        installationGroupId: t.installationGroupId,
        positionNumber: t.positionNumber,
      };
    case "empty":
      return {
        kind: "empty",
        positionNumber: t.positionNumber,
      };
  }
}

function encodeLegendRow(r: LegendRow): Record<string, unknown> {
  return {
    left: r.left.map(encodeLegendTile),
    right: r.right === null ? null : r.right.map(encodeLegendTile),
  };
}

export function encodeLegendDocument(doc: LegendDocument): Record<string, unknown> {
  return {
    panelId: doc.panelId,
    layoutSnapshot: {
      columns: doc.layoutSnapshot.columns,
      rows: doc.layoutSnapshot.rows,
      numberingScheme: doc.layoutSnapshot.numberingScheme,
      tandemIdScheme: doc.layoutSnapshot.tandemIdScheme,
    },
    preset: doc.preset,
    rows: doc.rows.map(encodeLegendRow),
    generatedAt: doc.generatedAt,
  };
}

export function decodeLegendDocument(input: unknown): LegendDocument {
  const parsed = legendDocumentJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeLegendDocument: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  return {
    panelId: j.panelId,
    layoutSnapshot: {
      columns: j.layoutSnapshot.columns,
      rows: j.layoutSnapshot.rows,
      numberingScheme: j.layoutSnapshot.numberingScheme,
      tandemIdScheme: j.layoutSnapshot.tandemIdScheme,
    },
    preset: j.preset,
    rows: j.rows.map(decodeRow),
    generatedAt: j.generatedAt,
    // R-SAFE-3 `safetyAdvisory` is a *derived* projection on
    // `LegendDocument`; the wire shape does not carry it. A consumer
    // that needs the advisory after decoding should rebuild from the
    // live panel against the dangerous-panel registry via
    // `buildPanelSafetyAdvisory`.
    safetyAdvisory: undefined,
  };
}

function decodeRow(j: LegendRowJson): LegendRow {
  return {
    left: j.left.map(decodeTile),
    right: j.right === null ? null : j.right.map(decodeTile),
  };
}

function decodeTile(j: LegendTileJson): LegendTile {
  switch (j.kind) {
    case "circuit":
      return {
        kind: "circuit",
        identifier: j.identifier,
        effectiveText: j.effectiveText,
        status: j.status,
        hasOpenWarning: j.hasOpenWarning,
        installationGroupId: j.installationGroupId,
        positionNumber: j.positionNumber,
        pairedWith: j.pairedWith,
      };
    case "continuation":
      return {
        kind: "continuation",
        headIdentifier: j.headIdentifier,
        installationGroupId: j.installationGroupId,
        positionNumber: j.positionNumber,
      };
    case "empty":
      return {
        kind: "empty",
        positionNumber: j.positionNumber,
      };
  }
}
